import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AxiosInstance from '../../../helpers/axiosInstance';
import { debounce } from 'lodash';
import { Table, Input, Pagination, Icon, Dropdown, Button, Dimmer, Loader } from 'semantic-ui-react';
import DocumentDownload from "../../../assets/images/document-download.svg";
import { Aggregation, DynamicSearchResponse, FilterData } from '../../../types/filterDataGridDTO';
import { ReactSVG } from "react-svg";
import styles from './DataGrid.module.css';
import axios from 'axios';

let resetPageNum = 0;
let searchTextCall = 0;
const CancelToken = axios.CancelToken;
let cancelTokenSource: any;

const DataGrid = <T,>({
  apiUrl,
  downloadURL,
  initialViewModelURL,
  additionalAggregations,
  columns,
  rowsPerPage,
  defaultSortByField = 'Subject',
  defaultSortOrderDesc = false,
  showSearch = true,
  searchPlaceholder = 'Search...',
  noDataMessage = 'No data to display',
  onPageChange,
  onSortChange,
  onSearchChange,
  onRecordsPerPageChange,
  rowKey,
  filters,
  setFilters,
  setAggregates,
  responseDataModel,
  setResponseDataModel,
  defaultFilter,
  reloadData,
  wardResponse,
}: DataGridProps<T>) => {
  const [searchParams] = useSearchParams();
  const queryUnreadValue = searchParams.get('unread');
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState<boolean | undefined>(false);
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortByField, setSortByField] = useState(defaultSortByField);
  const [sortOrderDesc, setSortOrderDesc] = useState(defaultSortOrderDesc);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(rowsPerPage);
  const [paginationInfo, setPaginationInfo] = useState({
    totalCount: 0,
    pageSize: rowsPerPage,
    currentPage: 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  });

  useEffect(() => {
    const responseData = AxiosInstance.get(`${initialViewModelURL}`);
    resetPageNum = 1;
    responseData.then(resp => {
      if (queryUnreadValue === '1') {
        resp.data.hasUnreadEmails = true;
        resp.data.pagination.sortByField = 'messageDate';
        resp.data.unread.isFocus = false;
        resp.data.unread.ids = [];
        resp.data.unread.ids.push(0);
        resp.data.unread.names = [];
        resp.data.unread.names.push('Unread messages');
        setSortByField('messageDate');
      }
      setResponseDataModel(resp.data);
      let filterObject: Record<string, FilterData> = {};
      if (resp.data.master && resp.data.master.mapper) {
        Object.keys(resp.data.master.mapper).map(key => {
          const keyValue = resp.data.master.mapper[key];
          filterObject[keyValue] = {
            ids: [], fromDate: null, isFocus: false, isFocusId: 0,
            names: [], rowOrder: 0, toDate: null, isNameFilter: resp.data[keyValue].isNameFilter
          } as FilterData;
        });
      }
      if (defaultFilter) {
        filterObject = { ...filterObject, ...defaultFilter };
      }
      if (queryUnreadValue === '1') {
        filterObject.unread.isFocus = false;
        filterObject.unread.ids = [];
        filterObject.unread.ids.push(0);
        filterObject.unread.names = [];
        filterObject.unread.names.push('Unread messages');
      }
      const dashboardFilter = localStorage.getItem('dashboardFilter');
      if (dashboardFilter !== null && apiUrl === '/Dashboard/CaseDatagrid') {
        const parsedFilters = JSON.parse(dashboardFilter) as Record<string, FilterData>;
        const updatedFilters = { ...filterObject };
        Object.keys(filterObject).forEach((key) => {
          if (parsedFilters.hasOwnProperty(key)) {
            updatedFilters[key] = parsedFilters[key];
          }
        });
        setFilters(updatedFilters);
      } else {
        setFilters(filterObject);
      }
    });
  }, []);

  useEffect(() => {
    if (responseDataModel) {
      const postParameter = { ...responseDataModel } as DynamicSearchResponse<T>;
      fetchData(postParameter);
    }
  }, [reloadData]);

  useEffect(() => {
    if (wardResponse) {
      const responseData = parseResponseData(wardResponse);
      setData(responseData.data);
      setTotalPages(responseData.totalPages);
      setPaginationInfo({
        totalCount: responseData.totalCount,
        pageSize: responseData.recordsPerPage,
        currentPage: responseData.currentPage,
        totalPages: responseData.totalPages,
        hasNext: responseData.hasNext,
        hasPrevious: responseData.hasPrevious,
      });
    }
  }, [wardResponse]);

  useEffect(() => {
    if (responseDataModel) {
      let postParameter = { ...responseDataModel, ...filters } as any;
      if (additionalAggregations) {
        postParameter = {
          ...postParameter, "caseDataGridTypeId": {
            "fromDate": null,
            "toDate": null,
            "ids": [
              additionalAggregations.caseDataGridTypeId
            ],
            "names": null,
            "isFocus": true,
            "isNameFilter": false
          }
        };
      }
      postParameter.searchResults && (postParameter.searchResults.results = []);
      postParameter.pagination && (postParameter.pagination.pageNumber = pageNum);
      postParameter.pagination && (postParameter.pagination.recordsPerPage = recordsPerPage);
      postParameter.pagination && (postParameter.pagination.sortByField = sortByField);
      postParameter.pagination && (postParameter.pagination.sortOrderDesc = sortOrderDesc);
      if (localStorage.getItem('isAdditionalAggregationsClicked') === '1') {
        if (postParameter.master && postParameter.master.mapper) {
          Object.keys(postParameter.master.mapper).map((key: string) => {
            postParameter[postParameter.master.mapper[key]].isFocus = false;
          });
        }
        (postParameter.searchResults?.aggregations || []).forEach(function (aggregation: any) {
          aggregation.isFocus = false;
        });
        localStorage.removeItem('isAdditionalAggregationsClicked');
      }
      delete postParameter.master;
      if (apiUrl === '/Dashboard/CaseDatagrid') {
        const dashboardFilter = localStorage.getItem('dashboardFilter');
        if (dashboardFilter !== null) {
          const parsedFilters = JSON.parse(dashboardFilter) as Record<string, FilterData>;
          postParameter.searchResults.aggregations = parsedFilters.searchResults.aggregations;
          localStorage.setItem('dashboardFilter', JSON.stringify(postParameter));
        } else {
          localStorage.setItem('dashboardFilter', JSON.stringify(postParameter));
        }
      }
      fetchData(postParameter);
    }
  }, [pageNum, sortByField, sortOrderDesc, recordsPerPage, additionalAggregations]);

  useEffect(() => {
    let postParameter = { ...responseDataModel, ...filters } as DynamicSearchResponse<T>;
    if (postParameter && Object.keys(postParameter).length > 0) {
      if (additionalAggregations && Object.keys(additionalAggregations).length > 0) {
        postParameter = {
          ...postParameter, "caseDataGridTypeId": {
            "fromDate": null,
            "toDate": null,
            "ids": [
              additionalAggregations.caseDataGridTypeId
            ],
            "names": null,
            "isFocus": false,
            "isNameFilter": false
          }
        };
      }
      postParameter.searchResults && (postParameter.searchResults.results = []);
      (postParameter.searchResults?.aggregations || []).forEach(function (aggregation) {
        if ((filters || {})[firstLower(aggregation.name)]?.isFocus) {
          aggregation.isFocus = true;
        } else {
          aggregation.isFocus = false;
        }
      });
      delete postParameter.master;

      let parsedFilters: Record<string, FilterData>;
      const dashboardFilter = localStorage.getItem('dashboardFilter');
      if (dashboardFilter !== null) {
        parsedFilters = JSON.parse(dashboardFilter);
      }
      Object.keys(postParameter).forEach((key) => {
        if (postParameter[key] && typeof postParameter[key] === 'object' && postParameter[key].hasOwnProperty('isNameFilter') && postParameter[key].isNameFilter) {
          postParameter[key].ids = [];
        }
        const allowedKeys = ["searchResults", "searchText", "pagination", "hasUnreadEmails"];
        if (apiUrl === '/Dashboard/CaseDatagrid' && parsedFilters && allowedKeys.includes(key) && parsedFilters.hasOwnProperty(key)) {
          postParameter[key] = parsedFilters[key];
        }
      });

      if (apiUrl === '/Dashboard/CaseDatagrid') {
        if (resetPageNum > 1) {
          postParameter.pagination!.pageNumber = 1;
        }
        (postParameter.searchResults?.aggregations || []).forEach(function (aggregation) {
          if ((filters || {})[firstLower(aggregation.name)]?.isFocus) {
            aggregation.isFocus = true;
          } else {
            aggregation.isFocus = false;
          }
        });
        localStorage.setItem('dashboardFilter', JSON.stringify(postParameter));
        resetPageNum++;
      }

      if (postParameter.searchText !== null) {
        fetchData(postParameter, postParameter.searchText);
      } else {
        fetchData(postParameter);
      }
    }
  }, [filters]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const debouncedSearch = useCallback(
    debounce((searchTextProp: string | null, postParameter: any) => {
      setPageNum(1);
      postParameter.searchResults && (postParameter.searchResults.results = []);
      if (apiUrl === '/Dashboard/CaseDatagrid') {
        postParameter.searchText = searchTextProp;
        localStorage.setItem('dashboardFilter', JSON.stringify(postParameter));
      }
      searchTextCall = 1;
      fetchData && fetchData(postParameter, searchTextProp);
      onSearchChange && onSearchChange(searchTextProp);
    }, 500),
    [onSearchChange, searchText]
  );

  function firstLower(lower: string) {
    return lower && lower[0].toLowerCase() + lower.slice(1) || lower;
  }

  const fetchData = async (requestDTOParam: DynamicSearchResponse<T> | null = null, searchTextProp: string | null = searchText) => {
    setIsLoading(true);
    try {
      // Cancel the previous request if it exists
      if (cancelTokenSource) {
        cancelTokenSource.cancel("Operation canceled due to a new request.");
      }

      // Create a new cancel token for the current request
      cancelTokenSource = CancelToken.source();
      
      // ... (your existing code for making the AJAX call)
      requestDTOParam && (requestDTOParam.searchText = searchTextProp);
      if (requestDTOParam?.unread && requestDTOParam?.unread.ids.length === 0 && requestDTOParam?.hasUnreadEmails) {
        requestDTOParam.hasUnreadEmails = false;
      }
      const response = await AxiosInstance.post(apiUrl, requestDTOParam, {
        cancelToken: cancelTokenSource.token,
      });

      if (apiUrl === '/Dashboard/CaseDatagrid') {
        const dashboardFilter = localStorage.getItem('dashboardFilter');
        if (dashboardFilter !== null) {
          const parsedFilters = JSON.parse(dashboardFilter) as Record<string, FilterData>;
          parsedFilters.searchResults.aggregations = response.data.searchResults.aggregations;
          localStorage.setItem('dashboardFilter', JSON.stringify(parsedFilters));
        }
      }

      setResponseDataModel(response.data);

      const responseData = parseResponseData(response);

      setData(responseData.data);
      if (searchTextCall === 0) {
        setSearchText(responseData.searchText);
      }
      searchTextCall = 0;
      setPageNum(responseData.currentPage);
      setTotalPages(responseData.totalPages);
      setPaginationInfo({
        totalCount: responseData.totalCount,
        pageSize: responseData.recordsPerPage,
        currentPage: responseData.currentPage,
        totalPages: responseData.totalPages,
        hasNext: responseData.hasNext,
        hasPrevious: responseData.hasPrevious,
      });
      if (setAggregates) {
        setAggregates(response.data.searchResults.aggregations);
      }
      onPageChange && onPageChange(pageNum);

    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const parseResponseData = (response: any): ResponseData<T> => {
    const data = response.data.searchResults.results;
    const paginationHeader = response.headers['x-pagination'];
    const pageInfo = paginationHeader ? JSON.parse(paginationHeader) : paginationInfo;
    const { HasNext, HasPrevious } = pageInfo;
    const { pageNumber, recordsPerPage, totalCount } = response.data.pagination;
    return {
      data,
      searchText: response.data.searchText,
      totalPages: Math.ceil(totalCount / recordsPerPage),
      totalCount: totalCount,
      recordsPerPage: recordsPerPage,
      currentPage: pageNumber,
      hasNext: HasNext,
      hasPrevious: HasPrevious,
    };
  };

  const handleHeaderClick = (field: string) => {
    if (field === sortByField) {
      setSortOrderDesc(!sortOrderDesc);
    } else {
      setSortByField(field);
      setSortOrderDesc(false);
    }
    onSortChange && onSortChange(field, !sortOrderDesc);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim() === '' ? null : event.target.value;
    setSearchText(value);
    debouncedSearch(value, responseDataModel);
  };

  const handlePaginationChange = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    data: any,
  ) => {
    setPageNum(data.activePage);
  };

  const handleRecordsPerPageChange = (
    event: React.SyntheticEvent<HTMLElement, Event>,
    data: any,
  ) => {
    setPageNum(1);
    setRecordsPerPage(data.value);
    onRecordsPerPageChange && onRecordsPerPageChange(data.value);
  };

  const handleClearSearch = () => {
    setSearchText('');
    // Clear the search text and immediately trigger a search
    debouncedSearch.cancel(); // Cancel any pending debounced search
    debouncedSearch(null, responseDataModel);
  };

  const downloadFile = async () => {
    try {
      if (downloadURL) {
        const postParameter = { ...responseDataModel, ...filters };
        delete postParameter.master;
        postParameter.searchResults && (postParameter.searchResults.results = []);
        const response: any = await AxiosInstance.post(downloadURL, postParameter);
        let filename = 'file.csv';
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = contentDisposition.match(filenameRegex);
          if (matches && matches.length > 1) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }
        const blob = new Blob([response.data]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const recordsPerPageOptions = [
    { key: '5', text: '5', value: 5 },
    { key: '10', text: '10', value: 10 },
    { key: '20', text: '20', value: 20 },
    { key: '50', text: '50', value: 50 },
  ];

  const isMobileView = screenWidth < 768;

  return (
    <>
      <div className="section__inner">
        {showSearch && (
          <div className={styles.searchInputWrapper}>
            <div style={{ position: 'relative' }}>
              <Input
                className={styles.search}
                value={searchText || ""}
                icon="search"
                iconPosition="left"
                placeholder={searchPlaceholder}
                onChange={handleSearchChange}
                style={{ width: '100%' }}
              />
              {searchText && searchText.length >= 1 && (
                <Icon
                  className={styles.searchClearIcon}
                  name="times"
                  link
                  onClick={handleClearSearch}
                />
              )}
            </div>
          </div>
        )}
        {downloadURL && (
          <div className="section__actions">
            {
              paginationInfo && paginationInfo.totalCount > 0 &&
              <Button className="btn-bright" onClick={() => downloadFile()}>
                <Button.Content visible>
                  <ReactSVG
                    src={DocumentDownload}
                  />
                </Button.Content>

                <Button.Content visible>
                  Download
                </Button.Content>
              </Button>
            }
          </div>
        )}
      </div>
      <div className="table-holder">
        <Dimmer.Dimmable as="div" dimmed={isLoading}>
          <Dimmer active={isLoading} inverted={true}>
            <Loader>Loading</Loader>
          </Dimmer>

          <Table celled={true} sortable={true} striped={true}>
            <Table.Header>
              <Table.Row>
                {columns.map((column, index) => (
                  <Table.HeaderCell
                    key={index}
                    sorted={column.field === sortByField ? (sortOrderDesc ? 'descending' : 'ascending') : undefined}
                    onClick={column.sortable ? () => handleHeaderClick(column.field as string) : undefined}
                    className={column.headerClassName ? column.headerClassName : null}
                    style={column.headerStyle ? column.headerStyle : null}
                  >
                    {column.field === 'isHighPriority' ? <Icon name='exclamation' color='grey' /> : column.header} {column.sortable ? <span className="table__sort">
                      {column.field === sortByField ? (sortOrderDesc ? undefined : undefined) : <Icon name="sort" />}
                    </span> : null}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.length > 0 && data.map((row) => (
                <Table.Row key={rowKey(row)}>
                  {columns.map((column, index) => (
                    <Table.Cell key={index}
                      className={column.className ? column.className : null}
                      style={column.style ? column.style : null}>{column.accessor(row)}</Table.Cell>
                  ))}
                </Table.Row>
              ))}

              {(!data || data.length === 0) &&
                <Table.Row key={1}>
                  <Table.Cell key={2} colSpan={columns.length}><p style={{ fontSize: '1.5em' }}>{noDataMessage}</p></Table.Cell>
                </Table.Row>
              }
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan={Math.floor(columns.length / 2)}>
                  <div className="select-pagination">
                    <span>Show</span>
                    <div className="select__inner">
                      <Dropdown
                        selection
                        options={recordsPerPageOptions}
                        value={recordsPerPage}
                        onChange={handleRecordsPerPageChange}
                      />
                    </div>
                  </div>
                  <div className='select-pagination' style={{ justifyContent: 'flex-end', float: 'right', position: 'relative', bottom: '2.5rem', right: '10rem' }}>Total {paginationInfo.totalCount}</div>
                </Table.HeaderCell>
                <Table.HeaderCell colSpan={Math.ceil(columns.length / 2)} floated='right'>
                  {paginationInfo && paginationInfo.totalPages > 0 &&
                    <Pagination
                      className={`floated right ${styles.hideOnMobile}`}
                      activePage={pageNum}
                      onPageChange={handlePaginationChange}
                      totalPages={paginationInfo.totalPages}
                      siblingRange={isMobileView ? 1 : 2}
                      boundaryRange={isMobileView ? 0 : 1}
                      firstItem={
                        (paginationInfo.hasPrevious && !isMobileView) ?
                          {
                            content: <Icon name="angle double left" />,
                            icon: true,
                            className: styles.hideOnMobile
                          }
                          : null
                      }
                      lastItem={
                        (paginationInfo.hasNext && !isMobileView) ?
                          {
                            content: <Icon name="angle double right" />,
                            icon: true, className:
                              styles.hideOnMobile
                          }
                          : null
                      }
                      prevItem={{
                        content: <Icon name="angle left" />,
                        icon: true,
                        className: styles.hideOnMobile,
                      }}
                      nextItem={{
                        content: <Icon name="angle right" />,
                        icon: true,
                        className: styles.hideOnMobile
                      }}
                      ellipsisItem={{
                        content: <Icon name="ellipsis horizontal" />,
                        icon: true,
                        className: styles.hideOnMobile,
                      }}
                    />
                  }
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </Dimmer.Dimmable>
      </div>
    </>
  );
};

export interface Column<T> {
  header: string;
  field: keyof T;
  sortable?: boolean;
  accessor: (row: any) => any;
  style?: any;
  className?: any;
  headerStyle?: any;
  headerClassName?: any;
}

export interface ResponseData<T> {
  data: T[];
  searchText: string;
  totalPages: number;
  totalCount: number;
  recordsPerPage: number;
  currentPage: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface DataGridProps<T> {
  apiUrl: string;
  downloadURL?: string;
  initialViewModelURL: string;
  additionalAggregations?: { [key: string]: any } | undefined;
  columns: Column<T>[];
  rowsPerPage: number;
  defaultSortByField?: string;
  defaultSortOrderDesc?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  onPageChange?: (pageNum: number) => void;
  onSortChange?: (field: string, desc: boolean) => void;
  onSearchChange?: (searchText: string | null) => void;
  onRecordsPerPageChange?: (recordsPerPage: number) => void;
  rowKey: (row: T) => any;
  noDataMessage: string;
  filters: Record<string, FilterData> | null;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, FilterData> | null>>;
  setAggregates?: React.Dispatch<React.SetStateAction<Aggregation[] | null>>;
  responseDataModel: DynamicSearchResponse<T> | null;
  setResponseDataModel: React.Dispatch<React.SetStateAction<DynamicSearchResponse<T> | null>>;
  defaultFilter?: Record<string, FilterData> | null;
  reloadData?: number;
  wardResponse?: any;
}

export default DataGrid;